"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Z_INDEX } from "@/lib/constants/z-index";
import { cn } from "@/utils/cn";
import { addOverlayOpenAttribute, removeOverlayOpenAttribute } from "@/utils/overlay-utils";
import { X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Dialog, Modal, ModalOverlay, type ModalOverlayProps } from "react-aria-components";
import { DragHandle } from "./drag-handle";
export interface OverlayProps extends Omit<ModalOverlayProps, "children" | "isOpen"> {
  /** Whether the overlay is open */
  isOpen: boolean;
  /** Callback fired when the overlay should close */
  onClose?: () => void;
  /** Content to display in the overlay */
  children: ReactNode;
  /** Additional CSS class name for the modal */
  className?: string;
  /** Whether the overlay can be dismissed by clicking outside or pressing escape */
  isDismissable?: boolean;
}
export function Overlay(t0) {
  const $ = _c(58);
  let children;
  let className;
  let isOpen;
  let onClose;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      isOpen,
      onClose,
      children,
      className,
      isDismissable: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = isOpen;
    $[4] = onClose;
    $[5] = props;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    isOpen = $[3];
    onClose = $[4];
    props = $[5];
    t1 = $[6];
  }
  const isDismissable = t1 === undefined ? true : t1;
  const isMobile = useIsMobile();
  const [dragY, setDragY] = useState(0);
  let t2;
  let t3;
  if ($[7] !== isMobile || $[8] !== isOpen) {
    t2 = () => {
      if (isOpen && isMobile) {
        addOverlayOpenAttribute();
      } else {
        removeOverlayOpenAttribute();
      }
      return _temp;
    };
    t3 = [isOpen, isMobile];
    $[7] = isMobile;
    $[8] = isOpen;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  if (isMobile) {
    let t4;
    if ($[11] !== onClose) {
      t4 = open => !open && onClose?.();
      $[11] = onClose;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = cn("fixed inset-0 flex items-end justify-center bg-black/50", Z_INDEX.OVERLAY);
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== className) {
      t6 = cn("relative w-full bg-white rounded-t-2xl shadow-xl z-10 max-h-[90vh]", "data-[entering]:animate-in data-[entering]:slide-in-from-bottom data-[entering]:duration-300 data-[entering]:ease-out", "data-[exiting]:animate-out data-[exiting]:slide-out-to-bottom data-[exiting]:duration-200 data-[exiting]:ease-in", className);
      $[14] = className;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    const t7 = dragY > 0 ? `translateY(${dragY}px)` : undefined;
    const t8 = dragY > 0 ? "none" : undefined;
    let t9;
    if ($[16] !== t7 || $[17] !== t8) {
      t9 = {
        transform: t7,
        transition: t8
      };
      $[16] = t7;
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== onClose) {
      t10 = () => onClose?.();
      $[19] = onClose;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <X className="h-6 w-6 text-gray-800" />;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== t10) {
      t12 = <Button variant="bareshell" className="absolute -top-14 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors z-50" onPress={t10} aria-label="Close dialog">{t11}</Button>;
      $[22] = t10;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t13;
    if ($[24] !== onClose) {
      t13 = <DragHandle onDrag={setDragY} onClose={() => onClose?.()} />;
      $[24] = onClose;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] !== children) {
      t14 = <div className="flex-1 overflow-y-auto p-2">{children}</div>;
      $[26] = children;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    if ($[28] !== t13 || $[29] !== t14) {
      t15 = <Modal><Dialog className="outline-none unfocusable h-full flex flex-col" aria-label="Overlay">{t13}{t14}</Dialog></Modal>;
      $[28] = t13;
      $[29] = t14;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    let t16;
    if ($[31] !== t12 || $[32] !== t15 || $[33] !== t6 || $[34] !== t9) {
      t16 = <div className={t6} style={t9}>{t12}{t15}</div>;
      $[31] = t12;
      $[32] = t15;
      $[33] = t6;
      $[34] = t9;
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    let t17;
    if ($[36] !== isDismissable || $[37] !== isOpen || $[38] !== props || $[39] !== t16 || $[40] !== t4) {
      t17 = <ModalOverlay isOpen={isOpen} onOpenChange={t4} isDismissable={isDismissable} className={t5} {...props}>{t16}</ModalOverlay>;
      $[36] = isDismissable;
      $[37] = isOpen;
      $[38] = props;
      $[39] = t16;
      $[40] = t4;
      $[41] = t17;
    } else {
      t17 = $[41];
    }
    return t17;
  }
  let t4;
  if ($[42] !== onClose) {
    t4 = open_0 => !open_0 && onClose?.();
    $[42] = onClose;
    $[43] = t4;
  } else {
    t4 = $[43];
  }
  let t5;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("fixed inset-0 flex bg-black/50", Z_INDEX.OVERLAY);
    $[44] = t5;
  } else {
    t5 = $[44];
  }
  let t6;
  if ($[45] !== className) {
    t6 = cn("relative w-[500px] max-w-[90dvw] h-full overflow-y-auto bg-white shadow-xl z-10", "data-[entering]:animate-in data-[entering]:slide-in-from-left data-[entering]:duration-300 data-[entering]:ease-out", "data-[exiting]:animate-out data-[exiting]:slide-out-to-left data-[exiting]:duration-200 data-[exiting]:ease-in", className);
    $[45] = className;
    $[46] = t6;
  } else {
    t6 = $[46];
  }
  let t7;
  if ($[47] !== children) {
    t7 = <Modal><Dialog className="h-full outline-none">{children}</Dialog></Modal>;
    $[47] = children;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  let t8;
  if ($[49] !== t6 || $[50] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[49] = t6;
    $[50] = t7;
    $[51] = t8;
  } else {
    t8 = $[51];
  }
  let t9;
  if ($[52] !== isDismissable || $[53] !== isOpen || $[54] !== props || $[55] !== t4 || $[56] !== t8) {
    t9 = <ModalOverlay isOpen={isOpen} onOpenChange={t4} isDismissable={isDismissable} className={t5} {...props}>{t8}</ModalOverlay>;
    $[52] = isDismissable;
    $[53] = isOpen;
    $[54] = props;
    $[55] = t4;
    $[56] = t8;
    $[57] = t9;
  } else {
    t9 = $[57];
  }
  return t9;
}
function _temp() {
  removeOverlayOpenAttribute();
}